/**
 * Kural setindeki ozel JavaScript govdelerini calistirir (property degeri ve `if` kosulu).
 *
 * Govde AsyncFunction ile derlenir; boylece `await` dogrudan yazilabilir.
 * Kullanicilar kodu farkli bicimlerde yaziyor, hepsi kabul edilir:
 *   return window.username;            -> fonksiyon govdesi
 *   window.username                    -> tek ifade (return'e sarilir)
 *   () => window.username              -> ok fonksiyonu (sonuc fonksiyon ise cagrilir)
 *   function () { return x; }          -> GTM tarzi fonksiyon ifadesi
 *   async () => { ... await ... }      -> async fonksiyon
 *
 * Guvenlik modeli GTM "Custom JavaScript Variable" ile ayni: kodu yalnizca
 * yetkili panel kullanicisi yazar ve kod kendi sitesinde calisir.
 *
 * CSP `unsafe-eval` yasakliysa derleme hata verir; bu durumda deger dusurulur
 * (editor test modunda ve panelde hata olarak gosterilir).
 */
import log2 from "../../../js/log2-lite";
import { JS_TIMEOUT_MS, LIMITS } from "./schema";

const log = log2("TRACKER_JS");

const AsyncFunction = Object.getPrototypeOf(async function () {}).constructor;

const TIMEOUT = { __supsisTimeout: true };

/** Govdenin aldigi parametreler; derleme ve fonksiyon dondurme durumunda ayni sirada verilir. */
const ARG_NAMES = ["el", "event", "page", "visitor", "rule", "props", "utils"];

/**
 * Derlenmis govdeler: { cacheKey: { body, fn } }.
 * Govde degisince yeniden derlenir, boylece panelde kod guncellenince
 * eski derleme kullanilmaz.
 */
const compileCache = {};

function wait(ms) {
    return new Promise(function (resolve) {
        setTimeout(resolve, ms);
    });
}

/** window altindaki bir yolu hata firlatmadan okur: get("window.user.plan") */
function readPath(path) {
    if (!path || typeof path !== "string") return undefined;
    const parts = String(path).replace(/^window\./, "").split(".");
    let current = window;
    for (let i = 0; i < parts.length; i++) {
        const key = parts[i].trim();
        if (!key) continue;
        if (current === null || current === undefined) return undefined;
        current = current[key];
    }
    return current;
}

/** "1.249,90 TL" / "$1,249.90" gibi metinleri sayiya cevirir. */
function toNumber(value) {
    if (typeof value === "number") return isNaN(value) ? null : value;
    if (value === null || value === undefined) return null;

    let text = String(value).replace(/[^\d,.\-]/g, "");
    if (!text) return null;

    const lastComma = text.lastIndexOf(",");
    const lastDot = text.lastIndexOf(".");
    if (lastComma > -1 && lastDot > -1) {
        // Hangi isaret sonda ise ondalik ayraci odur
        if (lastComma > lastDot) text = text.replace(/\./g, "").replace(",", ".");
        else text = text.replace(/,/g, "");
    } else if (lastComma > -1) {
        // Tek virgul: binlik mi ondalik mi? Son gruptaki hane sayisina bakilir
        text = text.slice(lastComma + 1).length === 3 ? text.replace(/,/g, "") : text.replace(",", ".");
    }

    const parsed = parseFloat(text);
    return isNaN(parsed) ? null : parsed;
}

function readCookie(name) {
    try {
        const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
        return match ? decodeURIComponent(match[1]) : null;
    } catch (e) {
        return null;
    }
}

/** js / if govdelerine verilen yardimci fonksiyonlar. */
export function buildUtils() {
    return {
        text: function (selector, root) {
            try {
                const node = (root || document).querySelector(selector);
                if (!node) return "";
                return String(node.innerText || node.textContent || "").replace(/\s+/g, " ").trim();
            } catch (e) {
                return "";
            }
        },
        attr: function (selector, name, root) {
            try {
                const node = (root || document).querySelector(selector);
                return node ? node.getAttribute(name) : null;
            } catch (e) {
                return null;
            }
        },
        closest: function (element, selector) {
            try {
                return element && element.closest ? element.closest(selector) : null;
            } catch (e) {
                return null;
            }
        },
        param: function (name) {
            try {
                return new URLSearchParams(window.location.search).get(name);
            } catch (e) {
                return null;
            }
        },
        cookie: readCookie,
        storage: function (key) {
            try {
                return window.localStorage.getItem(key);
            } catch (e) {
                return null;
            }
        },
        get: readPath,
        number: toNumber,
        wait: wait,
    };
}

/** Govdede `return` var mi (kaba kontrol; property adlari yanlis pozitif olmasin diye nokta hariclenir). */
function hasReturn(body) {
    return /(^|[^\w$.])return([^\w$]|$)/.test(body);
}

/**
 * Govde bir fonksiyon ifadesi mi: `() => ...`, `el => ...`, `function () {...}`, `async () => {...}`.
 * Bu durumda govde olarak degerlendirmek anlamsiz (fonksiyon uretilip atilir);
 * ifade dondurulup sonuc calistirilir.
 */
function isFunctionExpression(body) {
    return /^(async\s+)?(function\b|\(|[A-Za-z_$][\w$]*\s*=>)/.test(body);
}

/**
 * Denenecek govde varyantlari; ilk derlenen kullanilir.
 * Tek ifade varyanti `return (...)` ile sarilir, boylece `window.username` ve
 * `() => window.username` gibi yazimlar da deger uretir.
 */
function buildVariants(body) {
    const trimmed = String(body).trim();
    if (!trimmed) return [];

    const asExpression = "return (" + trimmed.replace(/;+\s*$/, "") + "\n);";
    if (isFunctionExpression(trimmed)) return [asExpression, trimmed];
    if (hasReturn(trimmed)) return [trimmed, asExpression];
    return [asExpression, trimmed];
}

/**
 * Govdeyi derler; sonuc govde ile birlikte cache'lenir.
 * @returns {Function|null} derleme basarisizsa null
 */
function compile(cacheKey, body) {
    const cached = compileCache[cacheKey];
    if (cached && cached.body === body) return cached.fn;

    let fn = null;
    let lastError = null;

    if (typeof body === "string" && body.length <= LIMITS.maxJsLength) {
        const variants = buildVariants(body);
        for (let i = 0; i < variants.length; i++) {
            try {
                fn = AsyncFunction.apply(null, ARG_NAMES.concat([variants[i]]));
                break;
            } catch (e) {
                // Sozdizimi hatasi veya CSP unsafe-eval kisiti
                lastError = e;
            }
        }
    }

    if (!fn) log.warn("js compile failed", { cacheKey: cacheKey, message: lastError && lastError.message });

    compileCache[cacheKey] = { body: body, fn: fn };
    return fn;
}

/**
 * Govdeyi calistirir.
 * @param {String} cacheKey ruleId + property (derleme cache'i icin)
 * @param {String} body fonksiyon govdesi, tek ifade veya fonksiyon ifadesi
 * @param {Object} context { el, event, page, visitor, rule, props }
 * @returns {Promise<Object>} { ok, value, error } — error: compileFailed | noValue | timeout | mesaj
 */
export function runJs(cacheKey, body, context) {
    const fn = compile(cacheKey, body);
    if (!fn) {
        return Promise.resolve({ ok: false, value: null, error: "compileFailed" });
    }

    const ctx = context || {};
    const args = [
        ctx.el || null,
        ctx.event || null,
        ctx.page || {},
        ctx.visitor || {},
        ctx.rule || {},
        ctx.props || {},
        buildUtils(),
    ];

    const execution = Promise.resolve()
        .then(function () {
            return fn.apply(null, args);
        })
        .then(function (value) {
            // Govde bir fonksiyon dondurduyse (ornek: () => window.username) cagrilip sonucu alinir
            if (typeof value !== "function") return value;
            return value.apply(null, args);
        });

    return Promise.race([
        execution,
        wait(JS_TIMEOUT_MS).then(function () {
            return TIMEOUT;
        }),
    ])
        .then(function (value) {
            if (value === TIMEOUT) return { ok: false, value: null, error: "timeout" };
            // Deger dondurmeyen kod sessizce dusmesin; panel "deger donmedi" diye gosterir
            if (value === undefined || value === null) return { ok: false, value: null, error: "noValue" };
            return { ok: true, value: value, error: null };
        })
        .catch(function (e) {
            return { ok: false, value: null, error: (e && e.message) || "runtimeError" };
        });
}

/** Kural seti degisince eski derlemeler birakilir. */
export function clearCompileCache() {
    const keys = Object.keys(compileCache);
    for (let i = 0; i < keys.length; i++) delete compileCache[keys[i]];
}

export { hasReturn, isFunctionExpression, buildVariants };

export { toNumber, readPath };



//////////////////
// WEBPACK FOOTER
// ./static/src/tracker/rules/jsRunner.js
// module id = TsQo
// module chunks = 1 2